import { PaymentMethodEnum, WalletPaymentMethod } from 'src/Constants/enums'
import {
  OrderCheckoutBreakdown,
  OrderCheckoutInfoResponse,
  OrderCheckoutItem,
  OrderItemType,
} from 'src/Redux/artistOrderApi'

type DisplayedPaymentMethodsParams = {
  availablePaymentMethods?: PaymentMethodEnum[]
  walletPaymentMethods: WalletPaymentMethod[]
  isOrderFullyCovered: boolean
  paypalEmail?: string
}

export const getIsOrderFullyCovered = (
  breakdown: OrderCheckoutBreakdown | undefined,
  isEnoughBalance: boolean,
): boolean => (breakdown ? breakdown.totalPayable === 0 : isEnoughBalance)

const BALANCE_EXCLUDED_ITEM_TYPES: OrderItemType[] = [
  OrderItemType.SpotifyPrioritizedLaunch,
  OrderItemType.TiktokPrioritizedLaunch,
]

const isBalanceExcludedItem = (item: OrderCheckoutItem): boolean =>
  BALANCE_EXCLUDED_ITEM_TYPES.includes(item.type)

export const getIsCoveredByBalance = (
  balance: number,
  checkoutInfo: OrderCheckoutInfoResponse | null | undefined,
): boolean => {
  if (!checkoutInfo) {
    return false
  }

  const { totalFullPrice } = checkoutInfo.breakdown

  return (
    totalFullPrice > 0 &&
    balance >= totalFullPrice &&
    !checkoutInfo.items.some(isBalanceExcludedItem)
  )
}

const FALLBACK_PAYMENT_METHOD_PRIORITY = [
  PaymentMethodEnum.Credits,
  PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT,
]

export const getFallbackPaymentMethod = (
  methods: PaymentMethodEnum[],
): PaymentMethodEnum =>
  FALLBACK_PAYMENT_METHOD_PRIORITY.find((method) => methods.includes(method)) ??
  methods[0]

export const getDisplayedPaymentMethods = ({
  availablePaymentMethods,
  walletPaymentMethods,
  isOrderFullyCovered,
  paypalEmail,
}: DisplayedPaymentMethodsParams): PaymentMethodEnum[] | undefined => {
  if (!availablePaymentMethods) {
    return void 0
  }

  const methods = availablePaymentMethods.filter((method) => {
    switch (method) {
      case PaymentMethodEnum.Credits:
        return isOrderFullyCovered
      case PaymentMethodEnum.STRIPE:
        return true
      case PaymentMethodEnum.PAYPAL:
        return !paypalEmail || !isOrderFullyCovered
      case PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT:
        return Boolean(paypalEmail)
      default:
        return false
    }
  })

  const wallets = isOrderFullyCovered ? [] : walletPaymentMethods

  return [...wallets, ...methods]
}

export const getIsConfirmDisabled = (
  isWalletProcessing: boolean,
  paymentMethod: PaymentMethodEnum,
  displayedPaymentMethods?: PaymentMethodEnum[],
): boolean => {
  if (isWalletProcessing) {
    return true
  }

  if (!displayedPaymentMethods) {
    return false
  }

  return (
    !displayedPaymentMethods.length ||
    !displayedPaymentMethods.includes(paymentMethod)
  )
}
